import React, { useState, useEffect, useRef } from "react";
import {
  fetchTournamentUpdatesList,
  createTournamentUpdate,
  updateTournamentUpdate,
  deleteTournamentUpdate,
} from "../../../api/tournamentControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import { GlobalButton, GlobalTextField } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalDataTable from "../../../../../global/GlobalDataTable";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";

/** Must match `mst_tournament_updates.update_name` VARCHAR(50). */
const TOURNAMENT_UPDATE_NAME_MAX_LEN = 50;

const TournamentUpdateMaster = ({ onDirectoryChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ id: null, update_name: "", description: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await fetchTournamentUpdatesList();
      setRows(res?.data || []);
    } catch (err) {
      setError(err.message || "Failed to load updates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const setFormField = (name, value) => {
    const next =
      name === "update_name"
        ? String(value ?? "").slice(0, TOURNAMENT_UPDATE_NAME_MAX_LEN)
        : value;
    setFormData((prev) => ({ ...prev, [name]: next }));
    setFormError(null);
  };

  const handleSubmit = async () => {
    setFormError(null);
    setSuccessMsg("");
    const trimmedName = formData.update_name.trim();
    if (!trimmedName) {
      setFormError("Update name is required.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      if (isEditing) {
        await updateTournamentUpdate(
          formData.id,
          { update_name: trimmedName, description: formData.description },
          csrfToken,
        );
        setSuccessMsg("Update label saved.");
      } else {
        await createTournamentUpdate(
          { update_name: trimmedName, description: formData.description },
          csrfToken,
        );
        setSuccessMsg("Update label created.");
      }
      setFormData({ id: null, update_name: "", description: "" });
      setIsEditing(false);
      await loadRows();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message || "Save failed");
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const ALERT_ID = "ms-tour-update-alert";

  if (loading) return <p className="ub-loading">Loading tournament updates…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <h3 className="ub-section__subhead" style={{ margin: "0 0 1rem 0" }}>
        Tournament update labels
      </h3>
      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit update" : "Add update"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton
                  variant="ghost"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ id: null, update_name: "", description: "" });
                  }}
                >
                  Cancel Edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save changes" : "Create Update"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id="tour_update_name"
            label="Update name"
            value={formData.update_name}
            onChange={(v) => setFormField("update_name", v)}
            maxLength={TOURNAMENT_UPDATE_NAME_MAX_LEN}
            required
          />
          <GlobalTextField
            id="tour_update_desc"
            label="Description"
            value={formData.description}
            onChange={(v) => setFormField("description", v)}
          />
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Tournament updates"
        headers={[
          { key: "n", label: "Name" },
          { key: "d", label: "Description" },
          {
            key: "a",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {rows.length === 0 ? (
          <tr>
            <td colSpan={3} style={{ textAlign: "center", padding: "1rem", color: "#6b7280" }}>
              No update labels yet.
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={r.id}>
              <td>{r.update_name}</td>
              <td>{r.description || "—"}</td>
              <td className="ub-table__col-actions">
                <div className="ub-row-actions">
                  <GlobalTableActionButton
                    action="edit"
                    onClick={() => {
                      setFormData({
                        id: r.id,
                        update_name: String(r.update_name ?? "").slice(
                          0,
                          TOURNAMENT_UPDATE_NAME_MAX_LEN,
                        ),
                        description: r.description || "",
                      });
                      setIsEditing(true);
                    }}
                  />
                  <GlobalTableActionButton
                    action="delete"
                    onClick={() => {
                      setItemToDelete(r);
                      setShowDelete(true);
                    }}
                  />
                </div>
              </td>
            </tr>
          ))
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDelete}
        onClose={() => !isDeleting && setShowDelete(false)}
        title="Remove update"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton onClick={() => setShowDelete(false)} disabled={isDeleting}>
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              loading={isDeleting}
              onClick={async () => {
                if (!itemToDelete) return;
                setIsDeleting(true);
                try {
                  let csrfToken = localStorage.getItem("csrf-token");
                  if (!csrfToken) {
                    csrfToken = await getCsrfToken();
                    localStorage.setItem("csrf-token", csrfToken);
                  }
                  await deleteTournamentUpdate(itemToDelete.id, csrfToken);
                  setShowDelete(false);
                  setItemToDelete(null);
                  setSuccessMsg("Update removed.");
                  await loadRows();
                  onDirectoryChange?.();
                } catch (e) {
                  alert(e.message || "Delete failed");
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              Confirm
            </GlobalButton>
          </div>
        }
      >
        <p>
          Deactivate <b>{itemToDelete?.update_name}</b>? It will no longer appear in tournament forms.
        </p>
      </GlobalModal>
    </div>
  );
};

export default TournamentUpdateMaster;
